/**
 * Tab de exceções de data (date overrides) e exceções recorrentes de um Event Type
 * 
 * Permite:
 * - Bloquear intervalos recorrentes por dia da semana (ex: almoço seg-sex)
 * - Bloquear datas específicas ou definir horários alternativos
 * 
 * RELEASE: 2026-04-10
 * VERSION: 2.0.0
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarOff, Plus, Trash2, Repeat, Calendar } from 'lucide-react';
import { useSchedulingDateOverrides, useSchedulingRecurringOverrides } from '@/hooks/useSchedulingEventTypes';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface EventTypeDateOverridesTabProps {
  eventTypeId: string;
}

/** Mapeamento de dias da semana */
const DAYS_OF_WEEK = [
  { value: 0, label: 'Dom', fullLabel: 'Domingo' },
  { value: 1, label: 'Seg', fullLabel: 'Segunda' },
  { value: 2, label: 'Ter', fullLabel: 'Terça' },
  { value: 3, label: 'Qua', fullLabel: 'Quarta' },
  { value: 4, label: 'Qui', fullLabel: 'Quinta' },
  { value: 5, label: 'Sex', fullLabel: 'Sexta' },
  { value: 6, label: 'Sáb', fullLabel: 'Sábado' },
];

export function EventTypeDateOverridesTab({ eventTypeId }: EventTypeDateOverridesTabProps) {
  // Hook de exceções pontuais (por data)
  const { overrides, isLoading, createOverride, deleteOverride, isCreating } =
    useSchedulingDateOverrides(eventTypeId);

  // Hook de exceções recorrentes (por dia da semana)
  const {
    recurringOverrides,
    isLoading: recurringLoading,
    createRecurringOverride,
    deleteRecurringOverride,
    isCreating: isCreatingRecurring,
  } = useSchedulingRecurringOverrides(eventTypeId);

  // ========== Estado do formulário de exceção pontual ==========
  const [newDate, setNewDate] = useState('');
  const [isBlocked, setIsBlocked] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');

  // ========== Estado do formulário de exceção recorrente ==========
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [recStartTime, setRecStartTime] = useState('12:00');
  const [recEndTime, setRecEndTime] = useState('13:00');
  const [recDescription, setRecDescription] = useState('');

  /** Adiciona exceção pontual */
  const handleAdd = async () => {
    if (!newDate) return;

    await createOverride({
      override_date: newDate,
      is_blocked: isBlocked,
      start_time: isBlocked ? undefined : startTime,
      end_time: isBlocked ? undefined : endTime,
    });

    // Reset do formulário
    setNewDate('');
    setIsBlocked(true);
  };

  /** Toggle de dia da semana na seleção múltipla */
  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  /** Adiciona exceção recorrente */
  const handleAddRecurring = async () => {
    if (selectedDays.length === 0 || !recStartTime || !recEndTime) return;

    await createRecurringOverride({
      days_of_week: selectedDays,
      is_blocked: true,
      start_time: recStartTime,
      end_time: recEndTime,
      description: recDescription || undefined,
    });

    // Reset do formulário
    setSelectedDays([]);
    setRecDescription('');
  };

  if (isLoading || recurringLoading) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">

      {/* ================================================================= */}
      {/* SEÇÃO 1: Exceções Recorrentes (por dia da semana) */}
      {/* ================================================================= */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Repeat className="h-5 w-5 text-primary" />
            Exceções Recorrentes
          </CardTitle>
          <CardDescription>
            Bloqueie intervalos de horário que se repetem toda semana (ex: horário de almoço)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Seleção de dias da semana */}
          <div className="space-y-2">
            <Label>Dias da semana</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm font-medium transition-colors border
                    ${selectedDays.includes(day.value)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                    }
                  `}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Horários do bloqueio recorrente */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Início do bloqueio</Label>
              <Input
                type="time"
                value={recStartTime}
                onChange={(e) => setRecStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Fim do bloqueio</Label>
              <Input
                type="time"
                value={recEndTime}
                onChange={(e) => setRecEndTime(e.target.value)}
              />
            </div>
          </div>

          {/* Descrição opcional */}
          <div className="space-y-2">
            <Label>Descrição (opcional)</Label>
            <Input
              placeholder="Ex: Horário de almoço"
              value={recDescription}
              onChange={(e) => setRecDescription(e.target.value)}
            />
          </div>

          <Button
            onClick={handleAddRecurring}
            disabled={isCreatingRecurring || selectedDays.length === 0}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {isCreatingRecurring ? 'Adicionando...' : 'Adicionar Bloqueio Recorrente'}
          </Button>

          {/* Lista de exceções recorrentes — layout agrupado por dia da semana */}
          {recurringOverrides.length > 0 && (() => {
            /** Agrupa overrides por dia da semana para exibição premium */
            const grouped = recurringOverrides.reduce<Record<number, typeof recurringOverrides>>((acc, ov) => {
              const day = ov.day_of_week ?? -1;
              if (!acc[day]) acc[day] = [];
              acc[day].push(ov);
              return acc;
            }, {});
            const sortedDays = Object.keys(grouped).map(Number).sort((a, b) => a - b);
            /** Formata horário removendo segundos (HH:MM:SS → HH:MM) */
            const fmtTime = (t: string) => t?.slice(0, 5) ?? t;

            return (
              <div className="mt-4 pt-4 border-t border-border/50 space-y-3">
                {/* Contagem resumida */}
                <p className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                  {sortedDays.length} {sortedDays.length === 1 ? 'dia' : 'dias'} com bloqueios ativos
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {sortedDays.map((dayValue) => {
                    const dayInfo = DAYS_OF_WEEK.find((d) => d.value === dayValue);
                    const items = grouped[dayValue];

                    return (
                      <div key={dayValue} className="flex rounded-lg bg-muted/30 overflow-hidden">
                        {/* Barra lateral primária */}
                        <div className="w-1 shrink-0 bg-primary rounded-l-lg" />

                        <div className="flex-1 px-3 py-2.5">
                          {/* Header do dia */}
                          <span className="text-xs font-semibold text-foreground">
                            {dayInfo?.fullLabel ?? `Dia ${dayValue}`}
                          </span>

                          {/* Intervalos de bloqueio */}
                          <div className="mt-1.5 space-y-1">
                            {items.map((ov) => (
                              <div
                                key={ov.id}
                                className="flex items-center gap-2 group"
                              >
                                {/* Chip de horário sutil */}
                                <span className="inline-flex items-center gap-1 rounded-md bg-destructive/10 text-destructive px-2 py-0.5 text-[11px] font-medium dark:bg-destructive/20">
                                  <span className="h-1.5 w-1.5 rounded-full bg-destructive/70 shrink-0" />
                                  {fmtTime(ov.start_time)} – {fmtTime(ov.end_time)}
                                </span>

                                {/* Descrição inline */}
                                {ov.description && (
                                  <span className="text-[11px] text-muted-foreground truncate">
                                    {ov.description}
                                  </span>
                                )}

                                {/* Botão deletar — aparece no hover */}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-auto opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all shrink-0"
                                  onClick={() => deleteRecurringOverride(ov.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* SEÇÃO 2: Exceções por Data (pontuais) */}
      {/* ================================================================= */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Exceções por Data
          </CardTitle>
          <CardDescription>
            Bloqueie uma data específica ou defina horários alternativos para dias específicos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={isBlocked}
                onCheckedChange={setIsBlocked}
              />
              <span className="text-sm text-foreground">
                {isBlocked ? 'Bloquear dia inteiro' : 'Horário alternativo'}
              </span>
            </div>
          </div>

          {/* Horários alternativos (visível quando não bloqueado) */}
          {!isBlocked && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Início</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Fim</Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleAdd}
            disabled={isCreating || !newDate}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {isCreating ? 'Adicionando...' : 'Adicionar Exceção'}
          </Button>
        </CardContent>
      </Card>

      {/* Lista de exceções por data existentes */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Exceções Configuradas</CardTitle>
          <CardDescription>
            {overrides.length === 0
              ? 'Nenhuma exceção por data configurada'
              : `${overrides.length} exceção(ões) por data configurada(s)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overrides.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-muted-foreground">
              <CalendarOff className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">Nenhuma exceção de data adicionada</p>
            </div>
          ) : (
            <div className="space-y-2">
              {overrides.map((override) => (
                <div
                  key={override.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-background"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-sm text-foreground">
                      {override.override_date
                        ? format(parseISO(override.override_date), "dd 'de' MMMM 'de' yyyy", {
                            locale: ptBR,
                          })
                        : '—'}
                    </span>
                    {override.is_blocked ? (
                      <Badge variant="destructive" className="text-xs">
                        Bloqueado
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {override.start_time} - {override.end_time}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteOverride(override.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
